import { useState, useEffect } from "react";
import { api, apiError } from "../lib/api";
import { toast } from "sonner";
import { fmtINR, fmtDate } from "../lib/format";
import { useAuth } from "../context/AuthContext";
import { X, Zap, Lock, Pencil, Calculator } from "lucide-react";

export default function RechargeModal({ circuit, onClose, onDone }) {
  const { can } = useAuth();
  const isPostpaid = circuit?.billing_type === "Postpaid";
  const today = new Date().toISOString().slice(0, 10);
  const [form, setForm] = useState({
    recharge_date: today,
    amount: circuit?.amount || "",
    payment_method: "UPI",
    reference_number: "",
    remarks: "",
    reason_for_delay: "",
  });
  const [preview, setPreview] = useState(null);
  const [override, setOverride] = useState(false);
  const [overrideDate, setOverrideDate] = useState("");
  const [overrideReason, setOverrideReason] = useState("");
  const [saving, setSaving] = useState(false);
  const overdue = circuit?.days_remaining !== null && circuit?.days_remaining < 0;
  const canOverride = can("edit") || can("verify") || can("recharge");

  useEffect(() => {
    document.body.style.overflow = "hidden";
    // Backend is the single source of truth for the next expiry.
    api.get(`/circuits/${circuit.id}/renewal-preview`).then((r) => {
      setPreview(r.data);
      setOverrideDate(r.data.calculated_next_expiry || "");
      if (r.data.requires_manual_date) setOverride(true);
    }).catch(() => {});
    return () => { document.body.style.overflow = ""; };
  }, [circuit.id]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    if (!form.amount || !form.recharge_date) { toast.error("Amount and date are required"); return; }
    if (override && !overrideDate) { toast.error("Please enter the actual next expiry date"); return; }
    if (override && preview && overrideDate !== preview.calculated_next_expiry && !overrideReason.trim()) {
      toast.error("A reason is required to override the calculated next expiry"); return;
    }
    setSaving(true);
    try {
      const payload = {
        recharge_date: form.recharge_date,
        amount: Number(form.amount),
        payment_method: form.payment_method,
        reference_number: form.reference_number,
        remarks: form.remarks,
        reason_for_delay: form.reason_for_delay,
      };
      if (override) {
        payload.new_expiry_date = overrideDate;
        payload.override = true;
        payload.override_reason = overrideReason;
      }
      const { data } = await api.post(`/circuits/${circuit.id}/recharge`, payload);
      toast.success(`${isPostpaid ? "Payment" : "Recharge"} recorded · next renewal ${fmtDate(data.next_expiry)}`);
      onDone?.();
      onClose();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    } finally { setSaving(false); }
  };

  const field = "mt-1 w-full h-10 px-3 rounded-lg border border-slate-300 focus:border-blue-500 outline-none text-sm";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-lg shadow-2xl max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="recharge-modal">
        <div className="flex items-start justify-between p-5 border-b border-slate-200 sticky top-0 bg-white">
          <div>
            <h3 className="font-head font-bold text-lg flex items-center gap-2"><Zap className="text-blue-600" size={20} /> {isPostpaid ? "Record Payment" : "Recharge Circuit"}</h3>
            <p className="text-sm text-slate-500 mt-0.5">{circuit.customer_name} · {circuit.vendor_name} · <span className="font-mono-num">{circuit.internal_circuit_id}</span></p>
          </div>
          <button onClick={onClose} className="p-1 text-slate-400 hover:text-slate-700" data-testid="recharge-close"><X /></button>
        </div>

        {overdue && (
          <div className="mx-5 mt-4 text-sm bg-red-50 border border-red-200 text-red-700 rounded-lg px-3 py-2">
            This circuit is <b>{Math.abs(circuit.days_remaining)} day(s) overdue</b>. Completing after expiry will be logged as a late renewal.
          </div>
        )}

        <div className="p-5 grid grid-cols-2 gap-4">
          <div>
            <label className="text-sm font-medium">Recharge Date</label>
            <input type="date" value={form.recharge_date} onChange={(e) => set("recharge_date", e.target.value)} className={field} data-testid="recharge-date" />
          </div>
          <div>
            <label className="text-sm font-medium">Amount (₹)</label>
            <input type="number" value={form.amount} onChange={(e) => set("amount", e.target.value)} className={field} data-testid="recharge-amount" />
          </div>
          <div>
            <label className="text-sm font-medium">Payment Method</label>
            <select value={form.payment_method} onChange={(e) => set("payment_method", e.target.value)} className={field} data-testid="recharge-method">
              {["UPI", "NetBanking", "Credit Card", "Debit Card", "Cash", "Vendor Portal", "Cheque"].map((m) => <option key={m}>{m}</option>)}
            </select>
          </div>
          <div>
            <label className="text-sm font-medium">Transaction / Ref No.</label>
            <input value={form.reference_number} onChange={(e) => set("reference_number", e.target.value)} className={field} placeholder="UTR / txn id" data-testid="recharge-ref" />
          </div>
          {overdue && (
            <div className="col-span-2">
              <label className="text-sm font-medium">Reason for Delay</label>
              <select value={form.reason_for_delay} onChange={(e) => set("reason_for_delay", e.target.value)} className={field} data-testid="recharge-reason">
                <option value="">Select…</option>
                {["Operational delay", "Vendor issue", "Payment gateway failed", "Unassigned", "Budget approval"].map((r) => <option key={r}>{r}</option>)}
              </select>
            </div>
          )}
          <div className="col-span-2">
            <label className="text-sm font-medium">Remarks</label>
            <textarea value={form.remarks} onChange={(e) => set("remarks", e.target.value)} rows={2} className="mt-1 w-full px-3 py-2 rounded-lg border border-slate-300 focus:border-blue-500 outline-none text-sm" data-testid="recharge-remarks" />
          </div>
        </div>

        {/* System-calculated next expiry block */}
        <div className="mx-5 mb-3 rounded-xl border border-blue-200 bg-blue-50/60 p-4" data-testid="next-expiry-block">
          <div className="flex items-center gap-2 text-blue-800 font-head font-bold text-sm mb-3"><Calculator size={16} /> System-Calculated Next Expiry</div>
          <div className="grid grid-cols-3 gap-3 text-sm">
            <div><div className="text-[11px] text-slate-500 uppercase">Previous Expiry</div><div className="font-mono-num font-semibold">{fmtDate(preview?.previous_expiry)}</div></div>
            <div><div className="text-[11px] text-slate-500 uppercase">Renewal Cycle</div><div className="font-semibold">{preview?.renewal_cycle || "—"}</div></div>
            <div><div className="text-[11px] text-slate-500 uppercase">Next Expiry</div><div className="font-mono-num font-extrabold text-blue-700" data-testid="calculated-next-expiry">{preview?.calculated_next_expiry ? fmtDate(preview.calculated_next_expiry) : "Manual"}</div></div>
          </div>

          {!override ? (
            <div className="mt-3 flex items-center justify-between">
              <span className="text-xs text-slate-500 flex items-center gap-1"><Lock size={12} /> Calculated automatically from the previous expiry &amp; cycle.</span>
              {canOverride && <button onClick={() => setOverride(true)} data-testid="change-next-expiry" className="text-xs font-semibold text-blue-700 flex items-center gap-1 hover:underline"><Pencil size={12} /> Change Next Expiry</button>}
            </div>
          ) : (
            <div className="mt-3 grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-medium">Actual Next Expiry</label>
                <input type="date" value={overrideDate} onChange={(e) => setOverrideDate(e.target.value)} className="mt-1 w-full h-9 px-2 rounded-lg border border-slate-300 text-sm" data-testid="override-date" />
              </div>
              <div>
                <label className="text-xs font-medium">Reason for Override</label>
                <select value={overrideReason} onChange={(e) => setOverrideReason(e.target.value)} className="mt-1 w-full h-9 px-2 rounded-lg border border-slate-300 text-sm" data-testid="override-reason">
                  <option value="">Select…</option>
                  {["ISP validity differs", "Plan changed", "Billing cycle changed", "Grace period", "Vendor correction", "Other"].map((r) => <option key={r}>{r}</option>)}
                </select>
              </div>
              {!preview?.requires_manual_date && (
                <button onClick={() => { setOverride(false); setOverrideDate(preview?.calculated_next_expiry || ""); setOverrideReason(""); }} className="col-span-2 text-xs text-slate-500 hover:underline text-left">↩ Use system-calculated date instead</button>
              )}
            </div>
          )}
        </div>

        <div className="p-5 border-t border-slate-200 flex justify-end gap-3 sticky bottom-0 bg-white">
          <button onClick={onClose} className="px-4 h-10 rounded-lg border border-slate-300 text-sm font-medium">Cancel</button>
          <button onClick={submit} disabled={saving || !preview} data-testid="recharge-submit" className="px-5 h-10 rounded-lg bg-[var(--brand)] hover:bg-[var(--brand-hover)] text-white text-sm font-semibold disabled:opacity-60">
            {saving ? "Saving…" : `Confirm & Complete`}
          </button>
        </div>
      </div>
    </div>
  );
}
